/* eslint-disable react/prop-types */
import pluralize from 'pluralize';
import { connect, useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import close from '../../../../assets/images/close.svg';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import {
  addToCart,
  clearProductConfig,
  removeIndexFromProductConfig,
  removeItemFromConfig,
  setProductConfig,
} from '../../../actions/v3Order';
import formatUSD from '../../../utils/formatUSD';
import { isJpgImage } from '../../../utils/imageHelpers';
import { getProductByLocation } from '../helpers/store';
import useScreenSize from '../hooks/screen';
import { SlotSelectorWrapper } from './productConfigStyles';
import useImageOrientation from '../../../hooks/useImageOrientation';
import { getPosesForSlot, getRequiredPoseCountForSlot } from './utils';

const SlotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 5px;
  height: 125px;
  width: 125px;
  font-weight: 800;
  align-items: center;
  justify-content: center;
  position: relative;
  border: ${(props) => (props.selected ? '2px solid #479DE3' : 'initial')};
  color: ${(props) => (props.selected ? '#479DE3' : 'initial')};
  overflow: hidden;
`;

const Slots = styled.div`
  display: grid;
  grid-template-columns: min-content min-content;
  grid-auto-rows: min-content;
  gap: 10px;
  margin-top: 15px;
`;

const SlotProductImage = styled.img`
  height: 85px;
`;

const SlotImageWrapper = styled.div`
  height: 85px;
  width: 65px;
  position: relative;
`;

const CloseIcon = styled.img`
  height: 13px;
  width: 13px;
  position: absolute;
  right: 8px;
  top: 10px;
  cursor: pointer;
`;

const BackgroundProductImage = styled.img`
  height: 85px;
`;

const PoseImage = styled.img`
  position: absolute;
  bottom: 0px;
  height: ${(props) => {
    return props.is_horizontal ? 'auto' : '85px';
  }};
  width: ${(props) => {
    return props.is_horizontal ? '85px' : 'auto';
  }};
  left: 50%;
  transform: translateX(-50%);
`;

const ProductTitle = styled.div`
  font-size: 12px;
  margin-top: 2px;
  text-align: center;
  color: #000000;
`;

const SlotTitle = styled.div`
  text-align: center;
  color: ${(props) => (props.free ? '#479DE3' : 'initial')};
`;

const Slot = ({
  number,
  product,
  currentSlot,
  background,
  pose,
  slotCount,
  location,
  history,
  freeIndices,
  imageOrientations,
  imageUrl,
}) => {
  const dispatch = useDispatch();
  const { studentPreviewImageUrl } = useSelector(
    (state) => state.additionalData,
  );

  const [productName, setProductName] = useState('');

  useEffect(() => {
    let _productName =
      product?.name.length > 26
        ? product?.name.substring(0, 26) + '...'
        : product?.name;
    setProductName(_productName);
  }, [product]);

  const onClickSlotWrapper = () => {
    if (location.hash !== '#slots') return;
    if (currentSlot === slotCount)
      return Swal.fire(`Please click on Done, Add to Cart.`);
    if (number !== currentSlot)
      return Swal.fire(
        `Please tap item ${currentSlot + 1} to begin configuring.`,
      );
    history.push(location.pathname);
  };

  const remove = (event) => {
    event.stopPropagation();
    dispatch(removeItemFromConfig(number, history));
  };

  return (
    <SlotWrapper selected={currentSlot === number} onClick={onClickSlotWrapper}>
      {product && <CloseIcon onClick={remove} src={close} />}
      {!product && !freeIndices?.includes(number) && (
        <SlotTitle>Item {number + 1}</SlotTitle>
      )}
      {!product && freeIndices?.includes(number) && (
        <SlotTitle free>
          FREE
          <br />
          Item {number + 1}
        </SlotTitle>
      )}
      {product && !background && !pose && (
        <SlotProductImage src={product.image_thumbnail} />
      )}
      {(background || pose) &&
        (() => {
          const poseImageUrl = pose?.url || studentPreviewImageUrl;
          const isJpg = poseImageUrl && isJpgImage(poseImageUrl);

          return (
            <SlotImageWrapper>
              {/* For JPGs: don't show background underneath (pose has no transparency) */}
              {/* For PNGs: show background so pose can overlay with transparency */}
              {background && !isJpg && (
                <BackgroundProductImage src={background.image_thumbnail} />
              )}
              {/* Always show pose/preview image when available */}
              {(pose || studentPreviewImageUrl) && poseImageUrl && (
                <PoseImage
                  src={poseImageUrl}
                  onContextMenu={(event) => event.preventDefault()}
                  is_horizontal={
                    imageUrl ? imageOrientations[imageUrl] || false : false
                  }
                />
              )}
            </SlotImageWrapper>
          );
        })()}
      {product && <ProductTitle>{productName}</ProductTitle>}
    </SlotWrapper>
  );
};

const ActionButton = styled.button`
  border: none;
  align-self: stretch;
  background: #479de3;
  height: 42px;
  padding: 10px;
  font-size: 14px;
  color: white;
  border-radius: 3px;
  font-weight: 800;
  margin-top: 30px;

  :disabled {
    background: gray;
    pointer-events: none;
    cursor: initial;
    opacity: 0.3;
  }
`;

const Instruction = styled.span`
  color: #479de3;
`;

const SlotSelector = ({
  actionButtonTitle = 'Done, Add to Cart',
  handleSubmit,
  shoot,
  shootProducts,
  productConfig,
  location,
  history,
  additionalData,
  pages,
  instruction = 'Select a Product',
  hasGalleries,
  ...props
}) => {
  const dispatch = useDispatch();

  const background_choice_mode = useSelector(
    (state) => state.shoot?.shoot?.background_choice_mode,
  );
  const isGreeenScreenEvent =
    background_choice_mode === 'advanced_greenscreen_backgrounds_only';

  const product = getProductByLocation(shoot?.shootProducts, location);
  const {
    name,
    price,
    maximum_children_count: slotCount,
    additional_data = {},
    additional_data: { free_indices = [] } = {},
    required_children_count,
    ...otherProperties
  } = product;

  const products = Object.values(productConfig).reduce(
    (a, b) => (b.product ? [...a, b.product] : a),
    [],
  );
  const backgrounds = Object.values(productConfig).reduce(
    (a, b) => (b.background ? [...a, b.background] : a),
    [],
  );
  // Poses by slot (array of arrays) to support 0, 1, or 2 poses per slot
  const posesBySlot = Array.from(Array(slotCount)).map((_, i) =>
    getPosesForSlot(productConfig[i]),
  );
  const poses = posesBySlot.flat();

  const imageUrls = [
    ...poses.map((pose) => {
      if (!pose) return null;
      return pose.selected_crop ? pose.selected_crop.url : pose.url;
    }),
  ].filter(Boolean);

  const { imageOrientations } = useImageOrientation(imageUrls || []);

  const isYouPickX = Object.keys(additional_data).length == 0;
  const isBYO = Object.keys(additional_data).length > 0;

  const minimumChildCount =
    isYouPickX && Number.isInteger(required_children_count)
      ? required_children_count
      : isYouPickX
      ? slotCount
      : 1;

  // Per-slot pose requirement: each slot must have its required number of poses (0, 1, or 2)
  const allPosesSatisfied = Array.from(Array(slotCount)).every((_, i) => {
    const config = productConfig[i];
    const required = getRequiredPoseCountForSlot(config, pages || []);
    return getPosesForSlot(config).length >= required;
  });
  const allBackgroundsSatisfied =
    backgrounds.length === 0 || backgrounds.length === products.length;

  const actionButtonEnabled =
    allPosesSatisfied &&
    allBackgroundsSatisfied &&
    products.length >= minimumChildCount;

  const [screenSize] = useScreenSize();

  const submitOrder = () => {
    handleSubmit((formState) => {
      if (isBYO) {
        if (products.length < minimumChildCount)
          return Swal.fire(
            `Please configure at least ${minimumChildCount} item(s).`,
          );

        if (products.length > backgrounds.length && isGreeenScreenEvent)
          return Swal.fire(`Please finish configuring all items.`);

        if (!allPosesSatisfied && hasGalleries) {
          return Swal.fire(`Please finish configuring all items.`);
        }
      }

      if (isYouPickX && !actionButtonEnabled) {
        return Swal.fire(
          `Please configure ${minimumChildCount} ${pluralize(
            'item',
            minimumChildCount,
          )}.`,
        );
      }

      const fullProduct = getProductByLocation(shoot.shootProducts, location);

      // Collect product options from all configured slots
      const allProductOptions = {};

      // For You-Pick products, we need to check each slot's productOptions
      Object.values(productConfig).forEach((config, index) => {
        if (config.productOptions) {
          // Store options with a key that includes the slot index
          Object.entries(config.productOptions).forEach(
            ([optionSetKey, optionValue]) => {
              allProductOptions[`slot${index}_${optionSetKey}`] = {
                productId: config.product?.id,
                productName: config.product?.name,
                optionSetKey,
                optionValue,
              };
            },
          );
        }
      });

      dispatch(
        addToCart({
          studentId: additionalData.studentId,
          fullproductData: fullProduct,
          requiredFields: formState,
          productOptions: allProductOptions,
        }),
      );
    })();
  };

  let priceToDisplay = price;

  if (isBYO) {
    let freeIndices = [];

    if (additional_data?.free_indices) {
      freeIndices = additional_data.free_indices;
    }

    priceToDisplay = products.reduce((sum, product, index) => {
      if (!freeIndices.includes(index)) {
        return sum + product.price;
      }
      return sum;
    }, 0);
  }

  return (
    <SlotSelectorWrapper>
      {(screenSize === 'Desktop' || location.hash === '#slots') && (
        <h3 style={{ wordBreak: 'break-word', margin: '0 0 15px' }}>
          {name} <br />
          {formatUSD(priceToDisplay)} - <Instruction>{instruction}</Instruction>
        </h3>
      )}
      <Slots>
        {Array.from(Array(slotCount)).map((_, i) => (
          <Slot
            key={`slot-${i}`}
            freeIndices={free_indices}
            slotCount={slotCount}
            location={location}
            currentSlot={parseInt(
              location.pathname.split('/').pop().replace('#slots', ''),
            )}
            pose={
              posesBySlot[i]?.[0]?.selected_crop
                ? posesBySlot[i][0].selected_crop
                : posesBySlot[i]?.[0]
            }
            background={backgrounds[i]}
            product={products[i]}
            shootProducts={shootProducts}
            shoot={shoot}
            history={history}
            additionalData={additionalData}
            number={i}
            {...props}
            imageOrientations={imageOrientations}
            imageUrl={
              posesBySlot[i]?.[0]?.selected_crop
                ? posesBySlot[i][0].selected_crop?.url
                : posesBySlot[i]?.[0]?.url || null
            }
          />
        ))}
      </Slots>
      <ActionButton onClick={submitOrder}>{actionButtonTitle}</ActionButton>
    </SlotSelectorWrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    additionalData: state.additionalData,
    shoot: state.shoot,
    order: state.v3Order,
    shootProducts: state.shoot.shootProducts,
    productConfig: state.v3Order.productConfig,
    hasGalleries: !!state.additionalData?.selectedStudent?.galleries,
  };
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      setProductConfig,
      clearProductConfig,
      removeItem: removeIndexFromProductConfig,
      addToCart: addToCart,
    },
    dispatch,
  );
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SlotSelector),
);
